import React, { useState, useEffect } from 'react';
import { FaDownload, FaSearch, FaCalendarAlt, FaClock, FaSignInAlt, FaSignOutAlt, FaTimes, FaInfoCircle } from 'react-icons/fa';
import useAxios from '../utils/useAxios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FiAlertTriangle,
  FiSunrise,
  FiSunset,
  FiArrowDownCircle,
  FiCoffee
} from 'react-icons/fi';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector'; 
import styles from '../styles/PresenceHistoriquePage'; 

const PresenceHistoriquePage = () => {
  const [presences, setPresences] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedType, setSelectedType] = useState(''); 
  const [stats, setStats] = useState({ present: 0, retard: 0, absent: 0, total: 0 });
  const [selectedStatut, setSelectedStatut] = useState('');
  const [selectedDate, setSelectedDate] = useState(''); 
  const [pageSize, setPageSize] = useState(10);
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  const [selectedImage, setSelectedImage] = useState(null); 
  const [motifModal, setMotifModal] = useState({ show: false, name: '', motif: '' });

  // États pour la gestion des organisations
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailJustif, setDetailJustif] = useState(null);

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailJustif(null);
  };

  const fetchOrganisations = async () => {
    try {
      const response = await axiosInstance.get('/organisations/');
      setOrganisations(response.data);
      
   
      if (response.data.length > 0) {
        const firstOrg = response.data[0];
        setSelectedOrganisation(firstOrg.id);
        setCurrentOrganisation(firstOrg);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des organisations", err);
    }
  };


  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);

    fetchPresences(1, orgId);
  };

  const generateMonths = () => {
    const months = [];
    const date = new Date();
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(date.getFullYear(), date.getMonth() - i, 1);
      const value = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      const label = monthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  const monthsList = generateMonths();

  const pointageTypes = [
    { value: '', label: 'Tous types' },
    { value: 'entree', label: 'Entrées' },
    { value: 'sortie', label: 'Sorties' }
  ];

  const fetchPresences = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return; 
    
    setLoading(true);
    try {
      const params = {
        page: targetPage,
        ...(selectedMonth && { month: selectedMonth }),
        ...(selectedType && { type: selectedType }),
        ...(selectedStatut && { statut: selectedStatut }),
        ...(search && { search }),
        ...(selectedDate && { date: selectedDate }),
        ...(orgId && { organisation: orgId }),
        page_size: pageSize === 'all' ? 1000 : pageSize 
      };

      const response = await axiosInstance.get(`/pointage/presence-history/`, { params });
      setPresences(response.data.results);
      
   
      const totalPages = pageSize === 'all' ? 1 : Math.ceil(response.data.count / pageSize);
      setPagination({
        totalPages: totalPages || 1,
      });
      setPage(targetPage);
    } catch (err) {
      console.error("Erreur lors de la récupération des présences", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    if (selectedOrganisation) {
      setPage(1);
      fetchPresences(1);
    }
  }, [selectedMonth, selectedType, selectedStatut, search, selectedDate, pageSize, selectedOrganisation]);

  const fetchAllPresencesForMonth = async () => {
    try {
      let allPresences = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params = {
          page: currentPage,
          ...(selectedMonth && { month: selectedMonth }),
          ...(selectedType && { type: selectedType }),
          ...(selectedStatut && { statut: selectedStatut }),
          ...(selectedDate && { date: selectedDate }),
          ...(selectedOrganisation && { organisation: selectedOrganisation }),
          page_size: 50 
        };

        const response = await axiosInstance.get(`/pointage/presence-history/`, { params });
        allPresences = [...allPresences, ...response.data.results];

        hasMore = currentPage * 50 < response.data.count;
        currentPage++;
      }

      if (search) {
        const searchTerm = search.toLowerCase();
        allPresences = allPresences.filter(p =>
          (p.employe_nom?.toLowerCase() || '').includes(searchTerm) ||
          (p.employe_prenom?.toLowerCase() || '').includes(searchTerm) ||
          (p.periode?.toLowerCase() || '').includes(searchTerm) ||
          `${p.employe_nom} ${p.employe_prenom}`.toLowerCase().includes(searchTerm)
        );
      }

      return allPresences;
    } catch (err) {
      console.error("Erreur lors de la récupération de toutes les présences", err);
      return [];
    }
  };

  useEffect(() => {
    const calculerStats = (presences) => {
      let present = 0, retard = 0, absent = 0;
      presences.forEach(p => {
        if (p.statut === 'PRESENT') present++;
        else if (p.statut === 'RETARD') retard++;
        else if (p.statut === 'ABSENT') absent++;
      });
      setStats({ 
        present, 
        retard, 
        absent,
        total: presences.length
      });
    };

    calculerStats(presences);  
  }, [presences]); 

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchPresences(newPage);
    }
  };

  const formatHeure = (heure) => {
    if (!heure) return '-';
    return heure.split('.')[0];
  };


  const handleExportPDF = async () => {
    if (!selectedMonth && !selectedDate) {
      alert("Veuillez sélectionner un mois ou une date avant d'exporter le PDF");
      return;
    }

    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Rapport historique des présences", 14, 20);

      const allPresences = await fetchAllPresencesForMonth();
      
      const totalStats = {
        present: allPresences.filter(p => p.statut === 'PRESENT').length,
        retard: allPresences.filter(p => p.statut === 'RETARD').length,
        absent: allPresences.filter(p => p.statut === 'ABSENT').length,
        total: allPresences.length
      };

      doc.setFontSize(12);
      if (selectedMonth) {
        doc.text(`Mois: ${monthsList.find(m => m.value === selectedMonth)?.label}`, 14, 30);
      }
      if (selectedDate) {
        doc.text(`Date: ${selectedDate}`, 14, selectedMonth ? 35 : 30);
      }
      if (currentOrganisation) {
        doc.text(`Organisation: ${currentOrganisation.nom}`, 14, selectedMonth || selectedDate ? 40 : 30);
      }
      
      doc.text(
        `Statistiques: Total: ${totalStats.total} | Présents: ${totalStats.present}  | Retards: ${totalStats.retard} | Absents: ${totalStats.absent}`,
        14,
        selectedMonth || selectedDate || currentOrganisation ? 45 : 35
      );

      const tableColumn = ["Nom", "Prénom", "Date", "Heure", "Type", "Période", "Statut", "Justifié"];
      const tableRows = [];

      allPresences.forEach((p) => {
        tableRows.push([
          p.employe_nom || '-',
          p.employe_prenom || '-',
          p.date || '-',
          formatHeure(p.heure),
          p.type_pointage === "entree" ? "Entrée" : "Sortie",
          p.periode === "autre" ? "Exceptionnelle" : p.periode.charAt(0).toUpperCase() + p.periode.slice(1).replace('_', ' '),
          p.statut || '-',
          (p.statut === "RETARD" || p.statut === "ABSENT")
            ? (p.est_justifiee ? "Oui" : "Non")
            : "-"
        ]);
      });

      const startY = selectedMonth || selectedDate || currentOrganisation ? 55 : 45;
      
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startY,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [27, 1, 155] },
      });

      const fileName = `historique_presence_${selectedMonth || selectedDate}_${currentOrganisation?.nom || ''}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };


  const openImageModal = (presence) => {
    setSelectedImage({
      src: presence.photo,
      name: `${presence.employe_prenom} ${presence.employe_nom}`
    });
  };


  const closeImageModal = () => {
    setSelectedImage(null);
  };    


  const openMotifModal = (presence) => {
    setMotifModal({
      show: true,
      name: `${presence.employe_prenom} ${presence.employe_nom}`,
      motif: presence.motif_sortie,
    });
  };

  const closeMotifModal = () => {
    setMotifModal({ show: false, name: '', motif: '' });
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>

    {showDetailModal && detailJustif && (
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color: '#1B019B' }}>
                Détail de la Justification de {detailJustif.employe_nom} {detailJustif.employe_prenom}
              </h5>
              <button className="btn-close" onClick={closeDetailModal}></button>
            </div>
            <div className="modal-body">
              <p><strong>Date de la présence :</strong> {detailJustif.date_presence}</p>
              <p><strong>Statut :</strong> {detailJustif.statut}</p>
              <p><strong>Texte de justification :</strong></p>
              <p style={{ 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '5px',
                whiteSpace: 'pre-wrap' 
              }}>
                {detailJustif.texte || 'Aucun texte fourni'}
              </p>

              {detailJustif.fichier && (
                <>
                  <p><strong>Fichier joint :</strong></p>
                  <a 
                    href={detailJustif.fichier} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#1B019B',
                      textDecoration: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#f0f0ff',
                      border: '1px solid #d0d0ff'
                    }}
                  >
                    <FaInfoCircle /> Voir le fichier joint
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )}


      {/* Modal pour afficher le motif */}
      {motifModal.show && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={closeMotifModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Motif de sortie - {motifModal.name}
                </h5>
                <button className="btn-close" onClick={closeMotifModal}></button>
              </div>
              <div className="modal-body">
                <p
                  style={{
                    fontSize: '1rem',
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {motifModal.motif}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour l'image agrandie */}
      {selectedImage && (
        <div style={styles.modalOverlay} onClick={closeImageModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={closeImageModal}>
              <FaTimes />
            </button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.name}
              style={styles.modalImage}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/default-avatar.png';
              }}
            />
            <div style={styles.employeeName}>{selectedImage.name}</div>
          </div>
        </div>
      )}

      <div className="text-center mb-3">
        <h3 style={styles.header}>Historique des Présences</h3>
        <hr style={styles.hr} />
      </div>

      {/* Header avec sélecteur d'organisation réutilisable */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Historique des présences pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={styles.title}>
            📋 HISTORIQUE - {currentOrganisation.nom}
          </h1>

          <div style={styles.statsContainer} className="mb-4">
            {/* Filtres principaux */}
            <div className="row g-3 mb-3 align-items-center">

              {/* Recherche */}
              <div className="col-12 col-md-4 d-flex align-items-center position-relative">
                <FaSearch className="me-2" style={{ color: '#1B019B' }} />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nom, Prénom ou Période..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchBar}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={styles.clearSearchButton}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Sélection du mois */}
              <div className="col-12 col-md-4 d-flex align-items-center">
                <FaCalendarAlt className="me-2" style={{ color: '#1B019B' }} />
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="form-select"
                  style={styles.selectFilter}
                >
                  <option value="">Tous les mois</option>
                  {monthsList.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sélection de la date */}
              <div className="col-12 col-md-4 d-flex align-items-center position-relative">
                <FaCalendarAlt className="me-2" style={{ color: '#1B019B' }} />
                <input
                  type="date"
                  className="form-control"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={styles.searchBar}
                />
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate('')}
                    style={styles.clearSearchButton}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Type de pointage */}
              <div className="col-12 col-md-4 d-flex align-items-center">
                <FaClock className="me-2" style={{ color: '#1B019B' }} />
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="form-select"
                  style={styles.selectFilter}
                >
                  {pointageTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Statut */}
              <div className="col-12 col-md-4 d-flex align-items-center">
                <FiAlertTriangle className="me-2" style={{ color: '#1B019B' }} />
                <select
                  value={selectedStatut}
                  onChange={(e) => setSelectedStatut(e.target.value)}
                  className="form-select"
                  style={styles.selectFilter}
                >
                  <option value="">Tous statuts</option>
                  <option value="PRESENT">Présent</option>
                  <option value="RETARD">Retard</option>
                  <option value="ABSENT">Absent</option>
                </select>
              </div>

              {/* Pagination / Nombre de lignes */}
              <div className="col-12 col-md-4 d-flex align-items-center">
                <label className="me-2 mb-0" style={{ color: '#1B019B', fontWeight: 500 }}>
                  Afficher :
                </label>
                <select
                  className="form-select"
                  style={styles.selectFilter}
                  value={pageSize}
                  onChange={(e) =>
                    setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
                  }
                  disabled={loading}
                >
                  <option value="10">10 lignes</option>
                  <option value="20">20 lignes</option>
                  <option value="50">50 lignes</option>
                  <option value="all">Tout afficher</option>
                </select>
              </div>
            </div>

            {/* Statistiques et export PDF */}
            <div className="row align-items-center gy-3 mt-3">
              {/* Badges statistiques */}
              <div className="col-12 col-md-8 d-flex flex-wrap gap-3">
                <span style={{ ...styles.statsBadge, borderLeft: '4px solid #28a745' }}>
                  Présents: <strong>{stats.present}</strong>
                </span>
                <span style={{ ...styles.statsBadge, borderLeft: '4px solid #ffc107' }}>
                  Retards: <strong>{stats.retard}</strong>
                </span>
                <span style={{ ...styles.statsBadge, borderLeft: '4px solid #dc3545' }}>
                  Absents: <strong>{stats.absent}</strong>
                </span>
              </div>

              {/* Bouton Export PDF */}
              <div className="col-12 col-md-4 text-md-end">
                <button
                  className="btn btn-danger"
                  onClick={handleExportPDF}
                  disabled={loading || (!selectedMonth && !selectedDate)}
                  title={!selectedMonth && !selectedDate ? "Veuillez sélectionner un mois ou une date pour exporter" : ""}
                  style={styles.exportButton}
                >
                  <FaDownload className="me-2" />
                  {loading ? 'Génération...' : 'Exporter PDF'}
                </button>
              </div>
            </div>
          </div>

          {/* Tableau des présences */}
          <div className="card shadow-sm mb-4" style={styles.card}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Photo</th> 
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Type</th>
                    <th>Période</th>
                    <th>Statut</th>
                    <th>Motif sortie</th>
                    <th>Justifié</th>
                  </tr>
                </thead>
                <tbody>
                  {presences.length ? presences.map((p) => (
                    <tr key={p.id}>
                      <td style={styles.photoCell}>
                        {p.photo ? (
                          <img
                            src={p.photo}
                            alt={`${p.employe_prenom} ${p.employe_nom}`}
                            style={styles.photo}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/default-avatar.png';
                            }}
                            onClick={() => openImageModal(p)}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'scale(1.1)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                            }}
                          />
                        ) : (
                          <div style={styles.defaultPhoto}>
                            {/* Icône par défaut */}
                          </div>
                        )}
                      </td>

                      <td>{p.employe_nom || '-'}</td>
                      <td>{p.employe_prenom || '-'}</td>
                      <td>{p.date || '-'}</td>
                      <td>{formatHeure(p.heure)}</td>
                      <td style={styles.tableCell}>
                        {p.type_pointage === 'entree' ? (
                          <>
                            <FaSignInAlt style={{ marginRight: '8px', color: '#28a745' }} />
                            Entrée
                          </>
                        ) : p.type_pointage === 'sortie' ? (
                          <>
                            <FaSignOutAlt style={{ marginRight: '8px', color: '#dc3545' }} />
                            Sortie
                          </>
                        ) : null}
                      </td>
                      <td>
                        {p.periode === "autre" ? (
                          <span
                            title={p.motif_sortie || 'Sortie hors période normale'}
                            className="text-warning fw-bold d-flex align-items-center gap-1"
                          >
                            <FiAlertTriangle /> Exceptionnelle
                          </span>
                        ) : p.periode === "matin" ? (
                          <span className="text-primary fw-bold d-flex align-items-center gap-1">
                            <FiSunrise /> Matin
                          </span>
                        ) : p.periode === "apres_midi" ? (
                          <span className="text-success fw-bold d-flex align-items-center gap-1">
                            <FiSunset /> Après-midi
                          </span>
                        ) : p.periode === "descente" ? (
                          <span className="text-danger fw-bold d-flex align-items-center gap-1">
                            <FiArrowDownCircle /> Descente
                          </span>
                        ) : p.periode === "pause" ? (
                          <span className="text-info fw-bold d-flex align-items-center gap-1">
                            <FiCoffee /> Pause
                          </span>
                        ) : (
                          <span className="fw-bold">
                            {p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace('_', ' ') || '-'}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${p.statut === "PRESENT" ? 'bg-success' : p.statut === "RETARD" ? 'bg-warning' : 'bg-danger'} rounded-pill`}>
                          {p.statut}
                        </span>
                      </td>

                      <td>
                        {p.motif_sortie ? (
                          <button
                            onClick={() => openMotifModal(p)}
                            style={styles.motifButton}
                          >
                            <FaInfoCircle /> Détail
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
    <td>
      {p.statut === "RETARD" || p.statut === "ABSENT" ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span className={`badge rounded-pill ${p.est_justifiee ? 'bg-success' : 'bg-danger'}`}>
            {p.est_justifiee ? "Oui" : "Non"}
          </span>
          {p.est_justifiee && p.justifications && p.justifications.length > 0 && (
            <button
              onClick={() => {
                const firstJustif = p.justifications[0];
                setDetailJustif({ 
                  ...firstJustif, 
                  employe_nom: p.employe_nom, 
                  employe_prenom: p.employe_prenom,
                  statut: p.statut,
                  date_presence: p.date
                });
                setShowDetailModal(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                fontSize: '0.75rem',
                backgroundColor: '#e9ecef',
                color: '#495057',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dee2e6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
            >
              <FaInfoCircle size={12} /> Détail
            </button>
          )}
        </div>
      ) : (
        "-"
      )}
    </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="10" className="text-muted py-4">
                        {loading ? 'Chargement en cours...' : 'Aucune donnée trouvée avec les filtres actuels.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0 flex-wrap justify-content-center">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Première
                      </button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo;
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &raquo;
                      </button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Dernière
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3 text-muted">Chargement des organisations...</p>
        </div>
      )}
    </div>
  );
};

export default PresenceHistoriquePage;